// ============================================================================
// @gs/config/copie -- LE GARDE DES SORTIES DE LA COPIE
// ============================================================================
// Lot feat-la-copie-coupe-ses-sorties. Parole de David, 2026-10-29 09 h 17 : « Avant d'ouvrir la
// copie pour la journee, on debranche ces 3 sorties [...] Il va falloir que ca soit claire ce sur
// quel copie sa sort ».
//
// La copie (emplacement `preprod`) porte les MEMES reglages que la production, sauf la base, son
// serveur et `GS_EMPLACEMENT` (tools/infrastructure/modules/gs-app.bicep), et la MEME identite
// (ADR-079 §3.3) : ni les reglages ni les droits ne la separent du monde reel. Ce garde le fait.
// Chaque point de passage d une sortie le consulte -- l envoyeur de courriel commun, les textos
// de ZoneSecur, @gs/storage, la file de synchronisation de gs-admin -- et
// `npm run check:la-copie-ne-sort-pas` refuse un point de passage qui ne le consulte pas.
//
// Sous-chemin a part (`@gs/config/copie`) : les temoins qui remplacent `@gs/config` par un faux
// ne le portent pas. Aucune dependance hors de zod.
import { z } from 'zod';
/** Le reglage COLLANT `GS_EMPLACEMENT`, lu aussi par getDeploymentSlot (index.ts). */
export const deploymentSlotSchema = z.object({
    GS_EMPLACEMENT: z.enum(['production', 'preprod']).default('production'),
});
const FORME_DE_CLE_D_APP = /^[a-z][a-z0-9-]{0,39}$/;
/** La cle de l app : `GS_APP_KEY` (ecrit dans le paquet par le flux commun), sinon le nom du site sans `gs-`. */
function cleDeLApp(env) {
    const declaree = (env.GS_APP_KEY ?? '').trim().toLowerCase();
    if (FORME_DE_CLE_D_APP.test(declaree))
        return declaree;
    const site = (env.WEBSITE_SITE_NAME ?? '').trim().toLowerCase().replace(/^gs-/, '');
    if (FORME_DE_CLE_D_APP.test(site))
        return site;
    return 'inconnue';
}
/**
 * `{ app }` quand cette instance tourne sur une copie, sinon `null`. FERME PAR DEFAUT (relecture
 * de securite du lot, A1) : la copie est reconnue des qu UN temoin le dit —
 *   - `GS_EMPLACEMENT` present et autre que `production` (`preprod`, mais aussi `Preprod`, une
 *     faute de frappe : un reglage derive coupe les sorties, il ne les rouvre jamais) ;
 *   - `WEBSITE_HOSTNAME` (pose par App Service) qui porte `-preprod-` ou `-preprod.` : l adresse de
 *     l emplacement, meme si le reglage collant a ete perdu.
 * Seule l absence des deux vaut production. A la difference de getDeploymentSlot (le bandeau,
 * qui ne s affiche que sur une declaration explicite), le doute coupe. Ne bloque jamais un demarrage.
 */
export function getCopie(env = process.env) {
    const declare = (env.GS_EMPLACEMENT ?? '').trim().toLowerCase();
    const parLeReglage = declare !== '' && declare !== 'production';
    // `gs-admin-preprod-hxdzhfcxaudqdsh9.canadacentral-01.azurewebsites.net` (az webapp deployment
    // slot list, 2026-10-28) : un tiret suit `preprod`, pas un point.
    const parLAdresse = /-preprod[-.]/i.test(env.WEBSITE_HOSTNAME ?? '');
    if (!parLeReglage && !parLAdresse)
        return null;
    return { app: cleDeLApp(env) };
}
/**
 * Le domaine des boites qui peuvent recevoir les courriels de la copie. Une boite d ailleurs
 * serait une sortie vers le monde : elle est refusee, et les courriels restent retenus.
 */
export const DOMAINE_DES_BOITES_DE_LA_COPIE = 'groupesignalisation.ca';
/** La boite de test par defaut (arbitrage de la tour du 2026-10-29, 09 h 44 : celle des programmeurs). */
export const BOITE_DE_TEST_PAR_DEFAUT = 'programmeur@groupesignalisation.ca';
/** Une adresse nue du domaine de l entreprise, ou `null`. */
function adresseDuDomaine(brute) {
    const forme = /^[a-z0-9._%+-]+@([a-z0-9.-]+)$/.exec(brute);
    return forme && forme[1] === DOMAINE_DES_BOITES_DE_LA_COPIE ? brute : null;
}
/**
 * L etat du courriel de la copie. `GS_COPIE_COURRIEL` absent = `boite-de-test` (le defaut) ;
 * `retenu` = retenu ; toute autre valeur = `retenu` (un reglage mal ecrit ferme, il n ouvre jamais).
 * Une boite `GS_COPIE_COURRIEL_VERS` posee mais hors du domaine de l entreprise ferme aussi.
 */
export function getEtatDuCourrielDeLaCopie(env = process.env) {
    const etat = (env.GS_COPIE_COURRIEL ?? '').trim().toLowerCase();
    if (etat !== '' && etat !== 'boite-de-test')
        return 'retenu';
    const vers = (env.GS_COPIE_COURRIEL_VERS ?? '').trim().toLowerCase();
    if (vers !== '' && !adresseDuDomaine(vers))
        return 'retenu';
    return 'boite-de-test';
}
/**
 * La boite qui recoit TOUS les courriels de la copie, ou `null` : alors rien ne part. Etat
 * `boite-de-test` : `GS_COPIE_COURRIEL_VERS` si posee (domaine de l entreprise seulement), sinon
 * la boite par defaut. Etat `retenu` : `null`. Jamais un vrai destinataire.
 */
export function getCourrielDeLaCopie(env = process.env) {
    if (getEtatDuCourrielDeLaCopie(env) === 'retenu') {
        const vers = (env.GS_COPIE_COURRIEL_VERS ?? '').trim();
        if (vers !== '' && !adresseDuDomaine(vers.toLowerCase())) {
            console.warn(`[gs/config] GS_COPIE_COURRIEL_VERS refuse (une adresse @${DOMAINE_DES_BOITES_DE_LA_COPIE} seulement) -- les courriels de la copie restent retenus`);
        }
        return null;
    }
    const vers = (env.GS_COPIE_COURRIEL_VERS ?? '').trim().toLowerCase();
    return vers !== '' ? vers : BOITE_DE_TEST_PAR_DEFAUT;
}
export function sortiesDeLaCopie(env = process.env) {
    if (!getCopie(env))
        return undefined;
    return {
        courriel: getEtatDuCourrielDeLaCopie(env),
        textos: 'retenu',
        fichiers: 'copie',
        synchronisation: 'coupee',
    };
}
/** Le conteneur de fichiers de la copie : celui de la production, suffixe `-copie`. */
export function conteneurDeLaCopie(conteneurDeLaProduction) {
    return `${conteneurDeLaProduction}-copie`;
}
//# sourceMappingURL=copie.js.map